import { Modal, Button, Table, Spin } from "antd";
import {
  CheckOutlined,
  CrownOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { FREE_FEATURES, PREMIUM_FEATURES } from "../../constants/Constant";
import { useEffect, useState } from "react";
import { getAllPlans } from "../../services/planService";
import { getFreeAutoSchedule } from "../../services/systemMessagesService";
import { useMediaQuery } from "react-responsive";

const UpgradeModal = ({ visible, onClose }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [freeAutoScheduleCount, setFreeAutoScheduleCount] = useState(5);

  // ✅ detect mobile screen
  const isMobile = useMediaQuery({ maxWidth: 768 });

  // Fetch plans and free auto schedule count from backend
  useEffect(() => {
    if (visible) {
      setLoading(true);

      Promise.all([getAllPlans(), getFreeAutoSchedule()])
        .then(([plansData, freeScheduleData]) => {
          setPlans(plansData);
          setFreeAutoScheduleCount(freeScheduleData.data || 5);
        })
        .catch((err) => {
          console.error("Failed to fetch data", err);
        })
        .finally(() => setLoading(false));
    }
  }, [visible]);

  // Update FREE_FEATURES with dynamic count
  const updatedFreeFeatures = FREE_FEATURES.map((feature) => {
    if (feature.includes("Auto-push schedules")) {
      return `Auto-push schedules (${freeAutoScheduleCount} leagues, switch anytime)`;
    }
    return feature;
  });

  // Collect all unique features (static + dynamic)
  const allFeatures = Array.from(
    new Set([...updatedFreeFeatures, ...PREMIUM_FEATURES, "Chat Credits"])
  );

  // ✅ Word wrapping based on screen size
  const wrapTextAfterWords = (text) => {
    const wordLimit = isMobile ? 2 : 3; // 2 words for mobile, 3 for desktop
    const processedText = text.replace(/auto-delete/g, "auto delete");
    const words = processedText.split(" ");

    if (words.length <= wordLimit) {
      return <div>{text}</div>;
    }

    const lines = [];
    for (let i = 0; i < words.length; i += wordLimit) {
      const line = words.slice(i, i + wordLimit).join(" ");
      const restoredLine = line.replace(/auto delete/g, "auto-delete");
      lines.push(restoredLine);
    }

    return (
      <div>
        {lines.map((line, index) => (
          <div key={index}>{line}</div>
        ))}
      </div>
    );
  };

  // ✅ Only green check and red cross
  const FeatureIcon = ({ included }) => {
    if (typeof included === "number") {
      return <span className="font-medium text-gray-700">{included} credits</span>;
    }
    if (included) {
      return <CheckOutlined className="text-lg text-green-500" />;
    }
    return <CloseOutlined className="text-lg text-red-500" />;
  };

  // Helper to clean "Plan" from names on mobile
  const formatPlanName = (name) => {
    if (isMobile) {
      return name.replace(/Plan/gi, "").trim();
    }
    return name;
  };

  // Build column definitions (Plans on top row)
  const columns = [
    {
      title: "Features",
      dataIndex: "feature",
      key: "feature",
      fixed: "left",
      width: isMobile ? 100:200,
      render: (feature) => (
        <div className="text-xs md:text-sm font-medium text-gray-700 whitespace-normal leading-snug">
          {wrapTextAfterWords(feature)}
        </div>
      ),
    },
    {
      title: (
        <div className="text-center">
          <div className="font-semibold text-gray-600 md:text-gray-800">
            {isMobile ? "Free" : "Free Plan"}
          </div>
          <div className="text-xl md:text-2xl font-bold text-gray-600 md:text-gray-800 mt-1">$0</div>
          <div className="text-xs text-gray-500">/ Year</div>
        </div>
      ),
      dataIndex: "free",
      key: "free",
      align: "center",
      render: (included) => <FeatureIcon included={included} />,
    },
    ...plans.map((plan) => ({
      title: (
        <div className="text-center">
          <div className="font-semibold text-gray-600 md:text-gray-800">
            {formatPlanName(plan.name)}
          </div>
          <div className="text-xl md:text-2xl font-bold text-[#e61f26] mt-1">${plan.price}</div>
          <div className="text-xs text-[#e61f26]">
            / {plan.durationInDays >= 365 ? "Year" : "Month"}
          </div>
        </div>
      ),
      dataIndex: plan._id,
      key: plan._id,
      align: "center",
      render: (included) => <FeatureIcon included={included} />,
    })),
  ];

  // Build rows (features on left, values for each plan)
  const dataSource = allFeatures.map((feature) => {
    const row = {
      key: feature,
      feature,
      free: feature === "Chat Credits" ? 0 : updatedFreeFeatures.includes(feature),
    };

    plans.forEach((plan) => {
      row[plan._id] = feature === "Chat Credits" ? plan.credits : true;
    });

    return row;
  });

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      className="upgrade-modal"
      centered
      styles={{
        body: { padding: 0 },
        header: { display: "none" },
      }}
    >
      <div className="px-0 py-5 md:p-6">
        {/* Header */}
        <div className="text-center mb-6 md:mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#e61f26] to-[#ff4757] rounded-full mb-4">
            <CrownOutlined className="text-2xl text-white" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
            Choose Your Plan
          </h2>
          <p className="text-gray-600 text-sm md:text-base">
            Compare plans and unlock the full potential of your experience
          </p>
        </div>

        {/* Feature Comparison Table */}
        <div className="mb-8 overflow-x-auto">
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={dataSource}
              pagination={false}
              className="feature-comparison-table"
              size="middle"
              bordered
              scroll={{ x: "max-content" }}
            />
          </Spin>
        </div>

        {/* Action Buttons */}
        <div className="grid md:grid-cols-1 gap-4">
          <Button
            type="primary"
            size="large"
            className="w-full h-12 bg-gradient-to-r from-[#e61f26] to-[#ff4757] border-none font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            onClick={() => {
              navigate("/recharge", { state: { page_status: "yearly" } });
              onClose();
            }}
          >
            Subscribe
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UpgradeModal;